import { useRef, useState } from "react";
import { DownloadIcon, IconButton, Menu, MenuItem, Tooltip } from "@flat-ui";
import {
  EXPORT_FORMAT_LABELS,
  EXPORT_FORMATS,
  ExportFormat,
} from "@utils/file-export";
import {
  ExportTableFilterProps,
  TableType,
} from "@utils/track-event/event-types";
import { GeneralTableEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** The table that is exported, used to track the event */
  tableType: TableType;

  /** The selection of the filters of the table, tracked with the event */
  filterProps?: ExportTableFilterProps;

  /** Called with the format the user picked */
  onExport(format: ExportFormat): void;
}

/** Download button opening a menu to pick the file format the table is exported to */
export function ExportMenuButton({
  tableType,
  filterProps,
  onExport,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  function handleSelect(format: ExportFormat): void {
    setIsOpen(false);

    trackEvent({
      name: GeneralTableEvents.exportTable,
      props: {
        location: tableType,
        format,
        ...filterProps,
      },
    });

    onExport(format);
  }

  return (
    <>
      <Tooltip title="Export">
        <IconButton
          ref={buttonRef}
          aria-label="Export"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(true)}
        >
          <DownloadIcon />
        </IconButton>
      </Tooltip>

      <Menu
        open={isOpen}
        anchorEl={buttonRef.current}
        // The default width of 250px is meant for longer labels than the format names
        minWidth={160}
        onClose={() => setIsOpen(false)}
      >
        {EXPORT_FORMATS.map((format) => (
          <MenuItem
            key={format}
            label={EXPORT_FORMAT_LABELS[format]}
            onClick={() => handleSelect(format)}
          />
        ))}
      </Menu>
    </>
  );
}
